import { AsyncPipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import type {
  OnyxOptional,
  OnyxRouteSummary,
  OnyxScheduleCompliance,
} from '@onyx/angular';
import {
  OnyxAmountPipe,
  OnyxButtonComponent,
  OnyxDistancePipe,
  OnyxDropdownDirective,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxRouteLegType,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { chain } from 'lodash';
import type { OrderPointEstimations } from '../../../../../dashboard/orders/common/interfaces/order-point';
import type { RouteSubpoint } from '../../../../interfaces/route/route-subpoint';
import { DurationPipe } from '../../../../pipes/duration.pipe';
import { DictionariesService } from '../../../../services/dictionaries.service';
import { RouteService } from '../../../../services/route.service';
import { TelematicsStore } from '../../../../stores/telematics.store';

export type RoutePointRouteSummary = Pick<
  OnyxRouteSummary,
  'distance' | 'duration' | 'fuelConsumption' | 'totalCost'
> &
  Pick<OrderPointEstimations, 'remainingDistance'> & {
    transitTypes: Exclude<OnyxRouteLegType, OnyxRouteLegType.VEHICLE>[];
    transitDuration: number | null;
    remainingTransitDuration: number | null;
  };

@Component({
  selector: 'app-route-point-route-summary',
  imports: [
    OnyxDistancePipe,
    DurationPipe,
    OnyxIconComponent,
    DecimalPipe,
    AsyncPipe,
    OnyxAmountPipe,
    NgTemplateOutlet,
    OnyxIconButtonComponent,
    OnyxTooltipDirective,
    OnyxDropdownDirective,
    TranslatePipe,
    OnyxButtonComponent,
  ],
  templateUrl: './route-point-route-summary.component.html',
  styleUrl: './route-point-route-summary.component.scss',
  host: {
    '[class.realization]': `!!realization() && showBordersButton()`,
  },
})
export class RoutePointRouteSummaryComponent implements OnInit {
  protected readonly telematicsStore = inject(TelematicsStore);
  private readonly routeService = inject(RouteService);
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);

  public readonly routeSummary =
    input.required<RoutePointRouteSummary | null>();
  public readonly realizationCompliance =
    input<OnyxOptional<OnyxScheduleCompliance>>();
  public readonly index = input.required<number>();
  public readonly isFirstLeg = input.required<boolean>();
  public readonly legType = input(OnyxRouteLegType.VEHICLE);
  public readonly variant = input<'default' | 'compact'>('default');
  public readonly subpoints = input<RouteSubpoint[]>();
  public readonly subpointsExpanded = input(false);
  public readonly showBordersButton = input(false);
  public readonly bordersExpanded = model(false);

  public readonly hasRealizationChange = output<boolean>();

  protected readonly I18N = 'route.summary';

  protected readonly OnyxRouteLegType = OnyxRouteLegType;

  protected readonly addOptions$ = this.routeService.addOptions$;
  protected readonly context = this.routeService.context;

  protected actionsAttached = signal(false);
  protected fuelUnit = toSignal(
    this.dictionariesService.getFuelUnit(
      this.routeService?.vehicle()?.fuelType,
    ),
  );

  protected telematicsItem = computed(() =>
    this.telematicsStore.getItem(this.routeService.vehicle()?.uuid),
  );
  protected subpointsIcon = computed(() =>
    chain(this.subpoints())
      .map((subpoint) => subpoint.icon)
      .uniq()
      .thru((icons) => {
        if (icons.length <= 2) return { icons, hiddenIcons: 0 };

        return { icons: [icons[0]], hiddenIcons: icons.length - 1 };
      })
      .value(),
  );
  protected realization = computed(() => {
    const summary = this.routeSummary();
    const compliance = this.realizationCompliance();
    if (!summary?.remainingDistance || !compliance) return null;

    const { remainingDistance, distance, remainingTransitDuration, duration } =
      summary;

    return {
      compliance,
      remainingDistance,
      remainingTransitDuration,
      remainingDuration: Math.round(duration * (remainingDistance / distance)),
    };
  });

  protected hasAdd = computed(
    () => this.routeService.hasAdd(this.index()) && this.isFirstLeg(),
  );

  public ngOnInit(): void {
    effect(() => this.hasRealizationChange.emit(!!this.realization()), {
      injector: this.injector,
    });
  }

  protected toggleSubpoints(): void {
    this.routeService.togglePointExpanded(this.index());
  }

  protected toggleBorders(): void {
    this.bordersExpanded.update((expanded) => !expanded);
  }
}
